import { useEffect, useRef, useState } from "react";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { useRealtimeEvent } from "../../lib/realtime";
import { ChevronLeft, Send, Paperclip } from "lucide-react";
import ReferralQuickModal from "../ReferralQuickModal";
import ReferralChatCard from "../ReferralChatCard";
import NewAppointmentModal from "../appointment/NewAppointmentModal";

const initials = (n) => (n || "?").split(" ").map((s) => s[0]).slice(0, 2).join("");
const hour = (iso) => {
  try {
    return new Date(iso).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit", hour12: true });
  } catch {
    return "";
  }
};

export const GlassAvatar = ({ c, size = 36 }) => (
  <span className="relative shrink-0 rounded-full overflow-hidden grid place-items-center bg-white/25 ring-1 ring-white/40" style={{ width: size, height: size }}>
    {c.photo_url ? (
      <img src={c.photo_url} alt={c.name} className="w-full h-full object-cover" />
    ) : (
      <span className="text-white font-semibold" style={{ fontSize: size / 3 }}>{initials(c.name)}</span>
    )}
    <span className={`absolute right-0 bottom-0 w-2.5 h-2.5 rounded-full ring-2 ring-white/70 ${c.online ? "bg-[#4ADE80]" : "bg-[#FF6B6B]"}`} />
  </span>
);

export const GlassConversation = ({ contact, onBack }) => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [peerTyping, setPeerTyping] = useState(false);
  const [refOpen, setRefOpen] = useState(false);
  const [apptPid, setApptPid] = useState(null);
  const endRef = useRef(null);
  const typingTimer = useRef(null);
  const lastTyping = useRef(0);
  const canRefer = user?.role === "doctor" && contact.role === "doctor";

  const fetchMsg = () => api.get(`/chat/${contact.id}`).then(({ data }) => setMessages(data)).catch(() => {});
  useEffect(() => {
    fetchMsg();
    const t = setInterval(fetchMsg, 10000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contact.id]);
  useRealtimeEvent("chat:new", (m) => { if (m.from_user_id === contact.id || m.to_user_id === contact.id) fetchMsg(); });
  useRealtimeEvent("chat:sent", (m) => { if (m.to_user_id === contact.id) fetchMsg(); });
  useRealtimeEvent("chat:typing", (m) => {
    if (m?.from_user_id !== contact.id) return;
    setPeerTyping(true);
    clearTimeout(typingTimer.current);
    typingTimer.current = setTimeout(() => setPeerTyping(false), 2500);
  });
  useEffect(() => { endRef.current?.scrollIntoView({ block: "end" }); }, [messages, peerTyping]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    const { data } = await api.post("/chat", { to_user_id: contact.id, content: text.trim() });
    setMessages((m) => [...m, data]);
    setText("");
  };
  const onType = (v) => {
    setText(v);
    if (v.trim() && Date.now() - lastTyping.current > 1500) {
      lastTyping.current = Date.now();
      api.post("/chat/typing", { to_user_id: contact.id }).catch(() => {});
    }
  };

  return (
    <div className="flex-1 min-h-0 flex flex-col cai-fade-in" data-testid={`cai-conversation-${contact.id}`}>
      <div className="px-5 pb-3 flex items-center gap-3">
        <button type="button" onClick={onBack} data-testid="cai-chat-back" className="w-8 h-8 rounded-full cai-glass cai-glass-hover grid place-items-center text-white">
          <ChevronLeft className="w-4 h-4" />
        </button>
        <GlassAvatar c={contact} size={36} />
        <div className="min-w-0">
          <div className="text-[13.5px] font-semibold text-white truncate">{contact.name}</div>
          <div className="text-[11px] text-white/75 truncate">
            {peerTyping ? "está escribiendo…" : `${contact.specialty || (contact.role === "doctor" ? "Doctor" : "")}${contact.online ? " · En línea" : ""}`}
          </div>
        </div>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto cai-scroll px-5 space-y-2">
        {messages.length === 0 && <div className="text-center text-[12px] text-white/70 py-6">Sin mensajes todavía.</div>}
        {messages.map((m) => {
          const mine = m.from_user_id === user.id;
          if (m.type === "referral") {
            return (
              <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                <ReferralChatCard message={m} isReceiver={!mine} onStatusChange={fetchMsg} onCreateAppointment={setApptPid} />
              </div>
            );
          }
          return (
            <div key={m.id} className={`flex flex-col ${mine ? "items-end" : "items-start"}`}>
              <div className={`max-w-[80%] rounded-[18px] px-3 py-2 text-[12.5px] whitespace-pre-wrap break-words ${mine ? "bg-white/90 text-[#2B4A6F] rounded-br-md" : "cai-glass text-white rounded-bl-md"}`}>
                {m.content}
              </div>
              <div className="mt-0.5 text-[9.5px] text-white/65 px-1">{hour(m.created_at)}{mine && (m.read ? " · Visto" : "")}</div>
            </div>
          );
        })}
        {peerTyping && (
          <div className="cai-glass rounded-[18px] rounded-bl-md px-3 py-2.5 inline-flex gap-1" data-testid="cai-chat-typing">
            <span className="typing-dot" /><span className="typing-dot" style={{ animationDelay: "0.15s" }} /><span className="typing-dot" style={{ animationDelay: "0.3s" }} />
          </div>
        )}
        <div ref={endRef} />
      </div>
      <form onSubmit={send} className="mx-5 mb-4 mt-2 cai-glass rounded-full h-12 flex items-center pl-4 pr-2 gap-2">
        <button
          type="button"
          disabled={!canRefer}
          onClick={() => setRefOpen(true)}
          title={canRefer ? "Enviar referido" : "Los referidos solo se envían entre doctores"}
          data-testid="cai-chat-referral"
          className="text-white/85 hover:text-white disabled:opacity-40"
        >
          <Paperclip className="w-[18px] h-[18px]" strokeWidth={1.6} />
        </button>
        <input
          value={text}
          onChange={(e) => onType(e.target.value)}
          placeholder="Escribe un mensaje…"
          data-testid="cai-chat-input"
          className="cai-input flex-1 min-w-0 text-[13.5px] text-white placeholder:text-white/70"
        />
        <button type="submit" disabled={!text.trim()} data-testid="cai-chat-send" className="w-8 h-8 grid place-items-center text-white hover:scale-110 transition-transform disabled:opacity-50">
          <Send className="w-[18px] h-[18px]" strokeWidth={1.6} />
        </button>
      </form>
      {refOpen && <ReferralQuickModal toDoctor={contact} onClose={() => setRefOpen(false)} onSent={fetchMsg} />}
      {apptPid && <NewAppointmentModal open onClose={() => setApptPid(null)} onCreated={() => setApptPid(null)} prefillPatientId={apptPid} />}
    </div>
  );
};
